'use client';

import { env } from '@cc-shopping/platform-env';
import { nullishToUndefinedable } from '@cc-shopping/utils/valibot';
import { datadogRum } from '@datadog/browser-rum';
import cookies from 'js-cookie';
import { useCallback, useEffect, useState } from 'react';
import * as v from 'valibot';
import { useAnalyticsPageView } from '../components/PageView/Context';
import { useAnalytics } from './useAnalytics';
const eoruResponseSchema = v.object({
  data: v.object({
    identity: v.object({
      cookie_id: v.string('cookie_id is required in identity'),
      created_at: v.string('created_at is required in identity'),
      email: nullishToUndefinedable(v.string('email must be a string when provided')),
      email_hash: nullishToUndefinedable(v.string('email_hash must be a string when provided')),
      field_metadata: nullishToUndefinedable(v.object({
        email: v.object({
          modified_at: nullishToUndefinedable(v.string('modified_at must be a string when provided')),
          modified_by: nullishToUndefinedable(v.string('modified_by must be a string when provided')),
          source: nullishToUndefinedable(v.string('source must be a string when provided'))
        }, 'email must be a valid object')
      }, 'field_metadata must be a valid object')),
      has_password: nullishToUndefinedable(v.boolean('has_password must be a boolean when provided')),
      id: v.string('id is required in identity'),
      identity_id: v.string('identity_id is required in identity'),
      identity_type: v.string('identity_type is required in identity'),
      roles: nullishToUndefinedable(v.array(v.string('role must be a string'), 'roles must be an array of strings')),
      shea_id: nullishToUndefinedable(v.string('shea_id must be a string when provided'))
    }, 'identity must be a valid object')
  }, 'data must be a valid object')
});
type IdentifyAdditionalSegmentAttributes = {
  eoru_registration_driver_page_name: string;
  eoru_registration_driver_location: string;
  eoru_registration_driver_sub_location?: string;
  eoru_registration?: {
    registration_driver_label: string;
    registration_ts: string;
  };
  latest_eoru_registered_at?: string;
  most_recent_traditional_device_id: string;
};
export type EORUResponse = v.InferInput<typeof eoruResponseSchema>['data'] | undefined;
const BASE_URL = env.NEXT_PUBLIC_NW_URL;
export const useEoru = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<EORUResponse>();
  const {
    analytics: segmentAnalytics,
    deployableName,
    gitSha,
    deployableVersion
  } = useAnalytics();
  const {
    pageViewId
  } = useAnalyticsPageView();
  useEffect(() => {
    void (async () => {
      try {
        const resp = await fetch(`${BASE_URL}api/visitor-identity/v1/identities/me`, {
          credentials: 'include',
          headers: {
            'X-Caller-Client-ID': 'cc-shopping-client'
          }
        });
        if (resp && ![401, 403, 472].includes(resp.status)) {
          const parsedResponse = v.safeParse(eoruResponseSchema, await resp.json());
          if (parsedResponse.success) {
            setData(parsedResponse.output.data);
          } else {
            datadogRum.addError(parsedResponse.issues[0], {
              message: 'Error parsing EORU response'
            });
          }
        }
      } catch (error) {
        datadogRum.addError(error, {
          message: 'Error fetching EORU'
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  const getCsrfToken = useCallback(() => {
    return cookies.get('csrf_token');
  }, []);
  const refreshCsrfToken = useCallback(async () => {
    try {
      await fetch(`${BASE_URL}identity/api/v1/init_csrf_session?caller_client_id=cc-shopping-client`, {
        headers: {
          'X-Caller-Client-ID': 'cc-shopping-client'
        },
        credentials: 'include'
      });
      return getCsrfToken();
    } catch (error_0) {
      datadogRum.addError(error_0, {
        message: 'Could not get CSRF Token for EORU creation'
      });
    }
  }, []);
  const createEORU = useCallback(async ({
    csrfToken,
    email,
    additionalSegmentAttributes,
    omitCredentials
  }: {
    csrfToken: string;
    email: string;
    additionalSegmentAttributes: IdentifyAdditionalSegmentAttributes;
    omitCredentials?: boolean;
  }) => {
    try {
      const response = await fetch(`${BASE_URL}api/visitor-identity/v1/register`, {
        method: 'post',
        credentials: omitCredentials ? 'omit' : 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Caller-Client-ID': 'cc-shopping-client',
          'X-Header-CSRF-Token': csrfToken,
          'X-Cookie-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          email,
          profiles: {
            registration_tracking_profile: {
              page_impression_id: pageViewId
            }
          }
        })
      });
      const json = await response.json();
      const parsedResponse_0 = v.parse(eoruResponseSchema, json);
      const {
        email_hash,
        identity_id,
        shea_id
      } = parsedResponse_0.data.identity;
      if (!segmentAnalytics) {
        datadogRum.addError(new Error('Segment instance not available.'), {
          message: 'No Segment instance to fire identify() analytics event.'
        });
      }
      segmentAnalytics?.identify(shea_id, {
        identify_call_type: 'eoru_registration',
        email,
        email_sha256: email_hash,
        latest_visitor_identity_id: identity_id,
        is_eoru: true,
        member_identity_id: identity_id,
        deployable_name: 'cc-shopping-client',
        deployable_version: deployableVersion,
        // below attributes are for registration only
        registration_driver_page_view_id: pageViewId,
        registration_driver_page_flow_name: '',
        latest_eoru_registered_at: new Date().toISOString(),
        eoru_registration_driver_page_url: typeof window !== 'undefined' ? window.location.href : '',
        // below attributes are for CMS pages (wp or others) taxonomy
        registration_driver_page_content_id: '',
        registration_driver_page_content_source: '',
        shea_id: shea_id,
        ...additionalSegmentAttributes
      }, {
        context: {
          app: {
            name: deployableName,
            version: gitSha
          }
        }
      });
      return parsedResponse_0;
    } catch (error_1) {
      datadogRum.addError(error_1, {
        message: 'Could not create EORU'
      });
    }
  }, [pageViewId, segmentAnalytics]);
  return {
    createEORU,
    data,
    getCsrfToken,
    loading,
    refreshCsrfToken
  };
};